import { api } from './client';

/**
 * Bookmark Folders API client for bookmark folder operations.
 */
export const bookmarkFoldersApi = {
  /**
   * List bookmark folders for a project
   * @param {string} projectId - Project UUID
   * @returns {Promise<Array>} List of folders with bookmark counts
   */
  listProject: (projectId) => api.get(`/projects/${projectId}/bookmark-folders`),

  /**
   * List bookmark folders for a story
   * @param {string} storyId - Story UUID
   * @returns {Promise<Array>} List of folders with bookmark counts
   */
  listStory: (storyId) => api.get(`/stories/${storyId}/bookmark-folders`),

  /**
   * Get a single bookmark folder by ID
   * @param {string} folderId - Folder UUID
   * @returns {Promise<Object>} Folder with bookmark count
   */
  get: (folderId) => api.get(`/bookmark-folders/${folderId}`),

  /**
   * Create a bookmark folder for a project
   * @param {string} projectId - Project UUID
   * @param {string} name - Folder name
   * @returns {Promise<Object>} Created folder
   */
  createProject: (projectId, name) =>
    api.post(`/projects/${projectId}/bookmark-folders`, { name }),

  /**
   * Create a bookmark folder for a story
   * @param {string} storyId - Story UUID
   * @param {string} name - Folder name
   * @returns {Promise<Object>} Created folder
   */
  createStory: (storyId, name) =>
    api.post(`/stories/${storyId}/bookmark-folders`, { name }),

  /**
   * Update a bookmark folder
   * @param {string} folderId - Folder UUID
   * @param {Object} updates - Fields to update (name, is_collapsed)
   * @returns {Promise<Object>} Updated folder
   */
  update: (folderId, updates) => api.patch(`/bookmark-folders/${folderId}`, updates),

  /**
   * Delete a bookmark folder.
   * Default: bookmarks stay, outside any folder.
   * withBookmarks: also delete the bookmarks in that folder.
   * @param {string} folderId - Folder UUID
   * @param {{ withBookmarks?: boolean }} [options]
   * @returns {Promise<Object>} { deleted_folder_id, bookmarks_moved_to_root, deleted_bookmark_ids }
   */
  delete: (folderId, options = {}) => {
    const path = options.withBookmarks === true
      ? `/bookmark-folders/${folderId}?with_bookmarks=true`
      : `/bookmark-folders/${folderId}`;
    return api.delete(path);
  },

  /**
   * Reorder a bookmark folder within its container
   * @param {string} folderId - Folder UUID to reorder
   * @param {string|null} afterId - ID of item to place after (folder or bookmark)
   * @param {string|null} beforeId - ID of item to place before (folder or bookmark)
   * @returns {Promise<Object>} Updated folder with new position
   */
  reorder: (folderId, afterId, beforeId) =>
    api.post(`/bookmark-folders/${folderId}/reorder`, { after_id: afterId, before_id: beforeId }),
};
